import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { api } from '../lib/api';
import Composer from '../components/Composer';
import FeedCard from '../components/FeedCard';
import OpenRequests from '../components/OpenRequests';
import { FindInput, FindResults } from '../components/FindBar';
import Loader from '../components/Loader';

/* Home is the post box and the feed — nothing else. Testers found everything
   around it (the weekly question, introductions, profile progress, filters)
   distracting from using the network to get something done. Those live in the
   right column now, or on People; "find it again" (My activity) and search too.

   `view` and `query` belong to the app shell, because the right column sets
   them. */

export const VIEWS = [['all', 'All posts'], ['requests', 'Requests'], ['mine', 'My posts'],
  ['commented', 'I commented'], ['mentions', 'Mentions me'], ['saved', 'Saved']];
const PAGE = 40;

/* Landmarks for a long scroll: Today · Yesterday · This week · Earlier this month · Month Year. */
function dayGroup(iso) {
  const d = new Date(iso);
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.floor((start - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'This week';
  if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) return 'Earlier this month';
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

const Home = forwardRef(function Home({
  notify, onOpenPost, onRequestsChanged, railTick, onGo, view = 'all', onViewChange, query = '', onQuery,
}, ref) {
  const [items, setItems] = useState(null);
  const requestsOnly = view === 'requests';
  const [updates, setUpdates] = useState([]);
  const [showUpdates, setShowUpdates] = useState(false);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const composerRef = useRef(null);
  const searching = query.trim().length > 0;

  const load = useCallback(async () => {
    try {
      const feedView = ['all', 'requests'].includes(view) ? 'all' : view;
      const [feed, ups] = await Promise.all([
        api.feed('post', PAGE, requestsOnly, feedView),
        api.feedUpdates().catch(() => []),
      ]);
      setItems(feed);
      setError('');
      setMore(view !== 'requests' && feed.length === PAGE);
      setUpdates(ups);
    } catch (e) {
      setError(e.message);
    }
  }, [view, requestsOnly]);

  async function loadMore() {
    if (!items?.length) return;
    setLoadingMore(true);
    try {
      const last = items[items.length - 1];
      const feedView = view === 'all' ? 'all' : view;
      const next = await api.feed('post', PAGE, false, feedView, byActivity ? (last.last_activity_at || last.created_at) : last.created_at);
      setItems((cur) => [...cur, ...next.filter((n) => !cur.some((c) => c.id === n.id))]);
      setMore(next.length === PAGE);
    } catch (e) { notify(e.message); } finally { setLoadingMore(false); }
  }

  useEffect(() => { load(); }, [load]);
  useImperativeHandle(ref, () => ({ focusComposer: () => composerRef.current?.open(), reload: load }));

  const label = VIEWS.find(([v]) => v === view)?.[1] || 'All posts';
  const byActivity = view !== 'all' && view !== 'requests';

  return (
    <>
      {/* Narrow windows hide the right column, so its tools come here. */}
      <div className="mobile-only home-narrow-tools">
        <FindInput value={query} onChange={onQuery} />
        <OpenRequests refresh={railTick} onOpen={onOpenPost} notify={notify} onChanged={load} />
      </div>

      {searching ? (
        <FindResults q={query} onClear={() => onQuery('')} onOpenPost={onOpenPost} onGo={onGo} notify={notify} />
      ) : (<>
        <Composer
          ref={composerRef}
          onPosted={(wasRequest, message) => {
            notify(message || (wasRequest ? 'Posted — it’s on your Open Requests' : 'Posted'));
            load();
            if (wasRequest) onRequestsChanged?.();
          }}
        />

        {/* What the feed shows: All, Requests, My posts… (was "My activity" in the right column). */}
        <div className="discover-divider">
          <span>Discover relevant posts</span>
          <label className="discover-filter">
            <span className="sr-only">Show</span>
            <select value={view} onChange={(e) => onViewChange(e.target.value)}>
              {VIEWS.map(([v, l]) => <option key={v} value={v}>{v === 'all' ? 'All' : l}</option>)}
            </select>
          </label>
        </div>

        {(view !== 'all' || updates.length > 0) && (
          <div className="feed-bar">
            {view !== 'all' && (
              <span className="feed-showing">
                Showing <b>{label}</b> · <button className="linklike" onClick={() => onViewChange('all')}>Show all posts</button>
              </span>
            )}
            {updates.length > 0 && (
              <button className="replies-pill" aria-expanded={showUpdates} onClick={() => setShowUpdates(!showUpdates)}>
                {updates.reduce((n, u) => n + u.count, 0)} new {updates.reduce((n, u) => n + u.count, 0) === 1 ? 'reply' : 'replies'} {showUpdates ? '▾' : '▸'}
              </button>
            )}
          </div>
        )}
        {showUpdates && updates.length > 0 && (
          <div className="card pad updates">
            {updates.slice(0, 6).map((u) => (
              <button key={u.post_id} type="button" className="update-row" onClick={() => onOpenPost(u.post_id)}>
                <span>{u.title}</span>
                <span className="muted-sm">{u.count} new · {u.people.join(', ')}</span>
              </button>
            ))}
          </div>
        )}

        {error && <div className="pe-error">{error}</div>}
        {items === null && !error && <Loader />}

        {items?.length === 0 && byActivity && (
          <div className="card pad empty-state">
            <b>{{ mine: 'You haven’t posted yet', commented: 'You haven’t commented on anything yet',
              mentions: 'Nobody has tagged you yet', saved: 'Nothing saved yet' }[view]}</b>
            <p className="muted-sm">
              {view === 'saved' ? 'Choose Save from a post’s ⋯ menu to keep it here — the easiest way to find it again.'
                : 'Posts show up here as soon as there are some.'}
            </p>
          </div>
        )}
        {items?.length === 0 && !byActivity && (
          <div className="card pad empty-state">
            <b>{requestsOnly ? 'No requests yet' : 'No posts yet'}</b>
            <p className="muted-sm">Say what you’re working on, or ask for help — Ask, Test, Offer, Intro, Looking For and Refer reach the people who can.</p>
          </div>
        )}

        {items?.map((item, i) => {
          const when = byActivity ? (item.last_activity_at || item.created_at) : item.created_at;
          const group = dayGroup(when);
          const prev = i > 0 ? dayGroup(byActivity ? (items[i - 1].last_activity_at || items[i - 1].created_at) : items[i - 1].created_at) : null;
          return (
            <React.Fragment key={item.id}>
              {group !== prev && <div className="day-divider"><span>{group}</span></div>}
              <FeedCard item={item} notify={notify} onOpen={onOpenPost}
                onRequestChanged={() => { load(); onRequestsChanged?.(); }}
                onSavedChanged={view === 'saved' ? load : undefined} />
            </React.Fragment>
          );
        })}

        {more && (
          <button className="btn ghost load-more" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load older posts'}
          </button>
        )}
      </>)}
    </>
  );
});

export default Home;
